import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronsRight } from "lucide-react";

/**
 * iOS-style swipe-to-confirm slider. The user drags a thumb from left to
 * right; releasing past ~85 % triggers `onConfirm`. When `disabled`, the
 * thumb is anchored to the left edge and the track is dimmed.
 *
 * Props:
 *   - label        : text shown in the track
 *   - onConfirm()  : async callback fired after a successful swipe
 *   - disabled     : when true, blocks any interaction
 *   - variant      : "primary" | "success" | "warning"  → colour tokens
 *   - testid       : root data-testid
 */
const VARIANTS = {
  primary: {
    track: "bg-primary/15 text-primary",
    thumb: "bg-primary text-primary-foreground",
    ring: "ring-primary/40",
  },
  success: {
    track: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
    thumb: "bg-[hsl(var(--success))] text-white",
    ring: "ring-[hsl(var(--success))]/40",
  },
  warning: {
    track: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
    thumb: "bg-[hsl(var(--warning))] text-white",
    ring: "ring-[hsl(var(--warning))]/40",
  },
};

export default function SlideToConfirm({
  label,
  onConfirm,
  disabled = false,
  variant = "primary",
  testid = "slide-to-confirm",
}) {
  const trackRef = useRef(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const startX = useRef(0);
  const maxX = useRef(0);
  const v = VARIANTS[variant] || VARIANTS.primary;

  const computeMax = () => {
    const w = trackRef.current?.offsetWidth || 0;
    // 56 px thumb + 8 px padding both sides
    maxX.current = Math.max(0, w - 64);
  };

  useEffect(() => {
    computeMax();
    const handler = () => computeMax();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const onStart = (clientX) => {
    if (disabled || confirming || confirmed) return;
    computeMax();
    startX.current = clientX - dragX;
    setDragging(true);
  };

  const onMove = (clientX) => {
    if (!dragging) return;
    const next = Math.max(0, Math.min(maxX.current, clientX - startX.current));
    setDragX(next);
  };

  const onEnd = async () => {
    if (!dragging) return;
    setDragging(false);
    if (dragX / Math.max(1, maxX.current) >= 0.85) {
      setConfirming(true);
      setDragX(maxX.current);
      try {
        if (navigator.vibrate) navigator.vibrate(40);
        await onConfirm?.();
        setConfirmed(true);
      } catch (_) {
        // restore on failure
        setDragX(0);
      } finally {
        setConfirming(false);
      }
    } else {
      setDragX(0);
    }
  };

  // Pointer events: unified mouse + touch
  const pointerDown = (e) => onStart(e.clientX);
  const pointerMove = (e) => onMove(e.clientX);
  const pointerUp = () => onEnd();

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => onMove(e.clientX);
    const up = () => onEnd();
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, dragX]);

  const ratio = maxX.current ? dragX / maxX.current : 0;
  const labelOpacity = Math.max(0.15, 1 - ratio * 1.4);

  return (
    <div
      ref={trackRef}
      data-testid={testid}
      className={`relative h-16 w-full select-none overflow-hidden rounded-full border ${
        disabled ? "border-border bg-muted/40 text-muted-foreground" : `border-transparent ${v.track}`
      } ${confirmed ? "opacity-90" : ""}`}
    >
      {/* Animated background fill */}
      <div
        className={`absolute inset-y-0 left-0 ${disabled ? "bg-muted-foreground/10" : v.thumb}`}
        style={{ width: `${dragX + 32}px`, transition: dragging ? "none" : "width 280ms ease-out" }}
      />
      {/* Centered label */}
      <div
        className="pointer-events-none absolute inset-0 flex items-center justify-center gap-1 text-sm font-semibold tracking-wide"
        style={{ opacity: confirmed ? 0 : labelOpacity, transition: "opacity 200ms ease-out" }}
      >
        {label}
        {!disabled && !dragging && (
          <span className="ml-1 inline-flex animate-pulse">
            <ChevronsRight className="h-4 w-4" />
            <ChevronsRight className="-ml-2.5 h-4 w-4" />
            <ChevronsRight className="-ml-2.5 h-4 w-4" />
          </span>
        )}
      </div>
      {/* Confirmation state */}
      {confirmed && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-semibold text-white">
          <Check className="mr-1.5 h-4 w-4" />
          Confirmé
        </div>
      )}
      {/* Thumb */}
      <button
        type="button"
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerUp}
        onPointerCancel={pointerUp}
        disabled={disabled || confirming || confirmed}
        className={`absolute top-1 z-10 flex h-14 w-14 items-center justify-center rounded-full ring-2 ring-offset-2 ring-offset-background transition-transform ${v.thumb} ${v.ring} ${
          disabled ? "cursor-not-allowed opacity-50" : "cursor-grab active:cursor-grabbing"
        } ${!disabled && !dragging && dragX === 0 ? "animate-pulse-soft" : ""}`}
        style={{
          left: `${4 + dragX}px`,
          transition: dragging ? "none" : "left 280ms ease-out",
          touchAction: "none",
        }}
        aria-label={label}
        data-testid={`${testid}-thumb`}
      >
        <ChevronsRight className="h-5 w-5" />
      </button>
    </div>
  );
}
